/**
 * Base API Service
 * Provides base functionality for API requests.
 * Authentication is handled by the backend using HttpOnly cookies.
 */

import { ApiError } from '../types/api/ApiError';

export class BaseApiService {
  protected baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || process.env.REACT_APP_API_BASE_URL || '';
  }

  /**
   * Make authenticated API request
   */
  protected async request<T>(
    endpoint: string,
    options: RequestInit = {},
    // kept for backwards compatibility; no longer used with cookie-based auth
    authenticated: boolean = true
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = new Headers(options.headers);

    headers.set('Content-Type', 'application/json');

    const response = await fetch(url, {
      ...options,
      headers,
      // Ensure cookies (including HttpOnly auth cookies) are sent with requests
      credentials: 'include',
    });

    if (!response.ok) {
      const error: ApiError = {
        message: `API request failed: ${response.statusText}`,
        status: response.status,
      };

      try {
        const errorData = await response.json();
        error.message = errorData.message || error.message;
        error.code = errorData.code;
      } catch {
        // If response is not JSON, use default error
      }

      throw error;
    }

    // Handle empty responses
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    return response.text() as unknown as T;
  }
}

